import classNames from "classnames";
import React from "react";
import { Trans, useTranslation } from "react-i18next";

import { cancelOrder } from "@repo/shopify/api/cancelOrder";
import { OrderStatusButtonType } from "@repo/shopify/types/enum";
import { extractId, sleep } from "@repo/shopify/utils/common";

import Button from "@atoms/Button";
import Link from "@atoms/Link";

import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useLanguage from "@hooks/useLanguage";

import useOrders from "@queries/useOrders";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

interface IProps {
  orderId: string;
  buttonType: OrderStatusButtonType;
  className?: string;
}

export default function OrderStatusButton({ orderId, buttonType, className }: IProps) {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const { openModal, openToast, closeAll, setIsLoading } = useLayoutStore();
  const { customer } = useCustomerStore();

  const { refetchOrders } = useOrders();

  const handleCancelOrder = async () => {
    try {
      setIsLoading(true);
      const response = await cancelOrder({
        shop: import.meta.env.VITE_REACT_APP_SHOP_NAME as string,
        customerId: extractId(customer.id),
        orderId,
        domain: lang,
      });

      if (response?.status === 200) {
        await sleep(1000);
        await refetchOrders();
        openToast(NOTIFICATIONS.SUCCESS_CANCEL_ORDER);
      } else {
        openToast(NOTIFICATIONS.ERROR_CANCEL_ORDER);
      }
    } catch (error) {
      openToast(NOTIFICATIONS.ERROR_CANCEL_ORDER);
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(message);
    } finally {
      closeAll();
      setIsLoading(false);
    }
  };

  const handleOpenCancelModal = () => {
    openModal({
      title: "Cancel Order",
      content: <Trans i18nKey="cancel_order_description" />,
      confirm: {
        label: "Confirm",
        onClick: () => {
          void handleCancelOrder();
        },
      },
      cancel: {
        label: "Cancel",
        onClick: () => {
          closeAll();
        },
      },
    });
  };

  return (
    <>
      {buttonType === OrderStatusButtonType.CANCEL && (
        <Button as="square" className={classNames("leading-none", className)} onClick={handleOpenCancelModal} variant="secondary">
          {t("cancel_order")}
        </Button>
      )}

      {buttonType === OrderStatusButtonType.RETURN && (
        <Link
          as="square"
          className={classNames("flex! items-center justify-center px-3! leading-none", className)}
          href={`${URLS.RETURN_REQUEST_DETAIL}/${orderId}`}
          state={{ orderId }}
          variant="secondary"
        >
          {t("request_return")}
        </Link>
      )}
    </>
  );
}
